import {
    IonButton,
    IonButtons,
    IonCard,
    IonCardContent,
    IonContent,
    IonHeader,
    IonIcon,
    IonItem,
    IonLabel,
    IonMenuButton,
    IonPage,
    IonSearchbar,
    IonSelect,
    IonSelectOption,
    IonSkeletonText,
    IonText,
    IonTitle,
    IonToolbar,
} from '@ionic/react';
import {addOutline, refreshOutline, warningOutline} from 'ionicons/icons';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import AsteriskMessageConfirmDialog from '../../components/admin/AsteriskMessageConfirmDialog';
import AsteriskMessageFormModal, {
    AsteriskMessageSaveResult,
} from '../../components/admin/AsteriskMessageFormModal';
import AsteriskMessageList from '../../components/admin/AsteriskMessageList';
import {AsteriskMessage, AsteriskMessageRequest, AsteriskMessageSourceType} from '../../model/AsteriskMessage';
import {createAsteriskMessagesApi, getAsteriskApiError} from '../../services/asteriskMessages';
import {useToast} from '../../utils/useToasts';
import './AdminAsteriskMessages.css';

const NOT_FOUND_STATUS = 404;

type FetchState = 'loading' | 'ready' | 'error';
type StatusFilter = 'ALL' | 'ENABLED' | 'DISABLED';
type SourceFilter = 'ALL' | AsteriskMessageSourceType;

const AdminAsteriskMessages: React.FC = () => {
    const api = useMemo(() => createAsteriskMessagesApi(), []);
    const showToast = useToast();
    const [messages, setMessages] = useState<AsteriskMessage[]>([]);
    const [fetchState, setFetchState] = useState<FetchState>('loading');
    const [fetchError, setFetchError] = useState('');
    const [refreshError, setRefreshError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
    const [sourceFilter, setSourceFilter] = useState<SourceFilter>('ALL');
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingMessage, setEditingMessage] = useState<AsteriskMessage>();
    const [messageToDelete, setMessageToDelete] = useState<AsteriskMessage>();
    const [isDeleting, setIsDeleting] = useState(false);
    const [pendingIds, setPendingIds] = useState<ReadonlySet<number>>(new Set());
    const pendingIdsRef = useRef(new Set<number>());
    const deletingRef = useRef(false);

    const loadMessages = useCallback(async (isInitial = false) => {
        if (isInitial) setFetchState('loading');
        try {
            const response = await api.getAll();
            setMessages(response);
            setFetchState('ready');
            setFetchError('');
            setRefreshError('');
        } catch (error: unknown) {
            const apiError = getAsteriskApiError(error);
            if (isInitial) {
                setFetchState('error');
                setFetchError(apiError.message);
                return;
            }
            setRefreshError(`De lijst kon niet worden ververst. ${apiError.message}`);
        }
    }, [api]);

    useEffect(() => {
        void loadMessages(true);
    }, [loadMessages]);

    const visibleMessages = useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLowerCase();
        return messages.filter((message) => {
            const matchesSearch = !normalizedSearch || [message.name, message.spokenText, message.displayText]
                .some((value) => value.toLowerCase().includes(normalizedSearch));
            const matchesStatus = statusFilter === 'ALL' ||
                (statusFilter === 'ENABLED' ? message.enabled : !message.enabled);
            const matchesSource = sourceFilter === 'ALL' || message.sourceType === sourceFilter;
            return matchesSearch && matchesStatus && matchesSource;
        });
    }, [messages, searchTerm, sourceFilter, statusFilter]);

    const activeCount = useMemo(() => messages.filter((message) => message.enabled).length, [messages]);

    const openCreateForm = () => {
        setEditingMessage(undefined);
        setIsFormOpen(true);
    };

    const openEditForm = (message: AsteriskMessage) => {
        setEditingMessage(message);
        setIsFormOpen(true);
    };

    const closeForm = () => {
        setIsFormOpen(false);
        setEditingMessage(undefined);
    };

    const saveMessage = async (request: AsteriskMessageRequest): Promise<AsteriskMessageSaveResult> => {
        try {
            if (editingMessage) await api.update(editingMessage.id, request);
            else await api.create(request);
            await loadMessages();
            showToast(editingMessage ? 'Telefoonbericht bijgewerkt' : 'Telefoonbericht aangemaakt', 'success');
            closeForm();
            return {success: true};
        } catch (error: unknown) {
            const apiError = getAsteriskApiError(error);
            if (apiError.status === NOT_FOUND_STATUS) {
                closeForm();
                showToast(apiError.message, 'danger');
                await loadMessages();
            }
            return {success: false, error: apiError};
        }
    };

    const toggleMessage = async (message: AsteriskMessage, enabled: boolean) => {
        if (pendingIdsRef.current.has(message.id)) return;
        pendingIdsRef.current.add(message.id);
        setPendingIds(new Set(pendingIdsRef.current));
        setMessages((current) => current.map((item) => item.id === message.id ? {...item, enabled} : item));
        try {
            await api.updateEnabled(message.id, {enabled});
            await loadMessages();
            showToast(`Telefoonbericht ${enabled ? 'geactiveerd' : 'gedeactiveerd'}`, 'success');
        } catch (error: unknown) {
            setMessages((current) => current.map((item) => item.id === message.id ? message : item));
            const apiError = getAsteriskApiError(error);
            showToast(apiError.message, 'danger');
            if (apiError.status === NOT_FOUND_STATUS) await loadMessages();
        } finally {
            pendingIdsRef.current.delete(message.id);
            setPendingIds(new Set(pendingIdsRef.current));
        }
    };

    const deleteMessage = async () => {
        if (!messageToDelete || deletingRef.current) return;
        deletingRef.current = true;
        setIsDeleting(true);
        try {
            await api.remove(messageToDelete.id);
            setMessageToDelete(undefined);
            await loadMessages();
            showToast('Telefoonbericht verwijderd', 'success');
        } catch (error: unknown) {
            const apiError = getAsteriskApiError(error);
            showToast(apiError.message, 'danger');
            if (apiError.status === NOT_FOUND_STATUS) {
                setMessageToDelete(undefined);
                await loadMessages();
            }
        } finally {
            deletingRef.current = false;
            setIsDeleting(false);
        }
    };

    return (
        <IonPage>
            <IonHeader>
                <IonToolbar>
                    <IonButtons slot="start"><IonMenuButton/></IonButtons>
                    <IonTitle>Telefoonberichten</IonTitle>
                </IonToolbar>
            </IonHeader>
            <IonContent fullscreen>
                <main className="asterisk-admin-content">
                    <section className="asterisk-page-heading" aria-labelledby="asterisk-page-title">
                        <div>
                            <h1 id="asterisk-page-title">Telefoonberichten</h1>
                            <p>Actieve berichten worden willekeurig tijdens een telefoongesprek afgespeeld.</p>
                        </div>
                        <IonButton onClick={openCreateForm}>
                            <IonIcon icon={addOutline} slot="start"/>Nieuw bericht
                        </IonButton>
                    </section>

                    {fetchState === 'loading' && (
                        <section className="asterisk-skeletons" aria-label="Telefoonberichten laden">
                            {[0, 1, 2].map((index) => (
                                <IonCard key={index}>
                                    <IonCardContent><IonSkeletonText animated/></IonCardContent>
                                </IonCard>
                            ))}
                        </section>
                    )}

                    {fetchState === 'error' && (
                        <IonCard className="asterisk-state-card" role="alert">
                            <IonCardContent>
                                <IonIcon icon={warningOutline}/>
                                <h2>Ophalen mislukt</h2>
                                <p>{fetchError}</p>
                                <IonButton onClick={() => void loadMessages(true)}>
                                    <IonIcon icon={refreshOutline} slot="start"/>Opnieuw proberen
                                </IonButton>
                            </IonCardContent>
                        </IonCard>
                    )}

                    {fetchState === 'ready' && (
                        <>
                            <section className="asterisk-summary" aria-label="Totalen">
                                <IonCard>
                                    <IonCardContent>
                                        <IonText color="medium">Totaal</IonText><strong>{messages.length}</strong>
                                    </IonCardContent>
                                </IonCard>
                                <IonCard>
                                    <IonCardContent>
                                        <IonText color="medium">Actief</IonText><strong>{activeCount}</strong>
                                    </IonCardContent>
                                </IonCard>
                            </section>
                            <section className="asterisk-controls" aria-label="Zoeken en filteren">
                                <IonSearchbar
                                    value={searchTerm}
                                    placeholder="Zoek in naam en teksten"
                                    aria-label="Zoek telefoonberichten"
                                    onIonInput={(event) => setSearchTerm(event.detail.value ?? '')}
                                />
                                <IonItem>
                                    <IonLabel>Status</IonLabel>
                                    <IonSelect
                                        value={statusFilter}
                                        interface="popover"
                                        aria-label="Filter op status"
                                        onIonChange={(event) => setStatusFilter(event.detail.value)}
                                    >
                                        <IonSelectOption value="ALL">Alle</IonSelectOption>
                                        <IonSelectOption value="ENABLED">Actief</IonSelectOption>
                                        <IonSelectOption value="DISABLED">Inactief</IonSelectOption>
                                    </IonSelect>
                                </IonItem>
                                <IonItem>
                                    <IonLabel>Brontype</IonLabel>
                                    <IonSelect
                                        value={sourceFilter}
                                        interface="popover"
                                        aria-label="Filter op brontype"
                                        onIonChange={(event) => setSourceFilter(event.detail.value)}
                                    >
                                        <IonSelectOption value="ALL">Alle</IonSelectOption>
                                        <IonSelectOption value="PRE_RECORDED">Bestaande opname</IonSelectOption>
                                        <IonSelectOption value="GENERATED">Gegenereerde spraak</IonSelectOption>
                                    </IonSelect>
                                </IonItem>
                            </section>
                            {refreshError && (
                                <div className="asterisk-refresh-error" role="alert">
                                    <span>{refreshError}</span>
                                    <IonButton fill="clear" onClick={() => void loadMessages()}>
                                        Opnieuw proberen
                                    </IonButton>
                                </div>
                            )}
                            {messages.length === 0 && (
                                <IonCard className="asterisk-state-card">
                                    <IonCardContent>
                                        <h2>Nog geen telefoonberichten</h2>
                                        <p>Maak het eerste bericht aan om te beginnen.</p>
                                    </IonCardContent>
                                </IonCard>
                            )}
                            {messages.length > 0 && visibleMessages.length === 0 && (
                                <IonCard className="asterisk-state-card">
                                    <IonCardContent>
                                        <h2>Geen resultaten</h2>
                                        <p>Pas je zoekopdracht of filters aan.</p>
                                    </IonCardContent>
                                </IonCard>
                            )}
                            {visibleMessages.length > 0 && (
                                <AsteriskMessageList
                                    messages={visibleMessages}
                                    pendingIds={pendingIds}
                                    onEdit={openEditForm}
                                    onDelete={setMessageToDelete}
                                    onToggle={(message, enabled) => void toggleMessage(message, enabled)}
                                />
                            )}
                        </>
                    )}
                </main>
                <AsteriskMessageFormModal
                    isOpen={isFormOpen}
                    message={editingMessage}
                    onDismiss={closeForm}
                    onSave={saveMessage}
                />
                <AsteriskMessageConfirmDialog
                    isOpen={Boolean(messageToDelete)}
                    title="Telefoonbericht verwijderen?"
                    message={(
                        <p>
                            Je verwijdert <strong>{messageToDelete?.name}</strong> definitief. Dit kan niet worden
                            teruggedraaid.
                        </p>
                    )}
                    confirmLabel="Definitief verwijderen"
                    busyLabel="Verwijderen…"
                    isBusy={isDeleting}
                    destructive
                    onCancel={() => setMessageToDelete(undefined)}
                    onConfirm={() => void deleteMessage()}
                />
            </IonContent>
        </IonPage>
    );
};

export default AdminAsteriskMessages;
